import * as React from "react";
import { Card, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";

import type { QuoteLine } from "@/hooks/api/use-campaign-requests";

import {
	amountOrNull,
	formatMoney,
	formatMoneyRange,
	formatQuantity,
	marketLabel,
	PRICE_CODE_LABELS,
	PRICE_UNIT_LABELS,
	QUOTE_LINE_GROUPS,
	quoteLineGroup,
	type QuoteLineGroup,
} from "../constants";
import { labelOf } from "../humanise";
import { formatRate } from "../price-book/rate-rows";

export interface QuoteLinesTableProps {
	lines: QuoteLine[];
	currency: string;
	/** The quote's own totals. Given them, the table can state the firm part and the range. */
	fixedTotal?: string | null;
	variableLow?: string | null;
	variableHigh?: string | null;
	emptyText?: string;
}

/** Decimal strings in, one number out. Coerced once, here, at the edge. */
function sumAmounts(lines: QuoteLine[]): number {
	return lines.reduce((total, line) => total + (amountOrNull(line.amount) ?? 0), 0);
}

/** Cents, as money is read. Everything printed on this table goes through here first. */
function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

/**
 * Split one end of the estimated band across the variable groups, in proportion to what each
 * group's lines actually come to.
 *
 * The last group takes the remainder rather than its own rounded share, so the printed group
 * figures add to the printed total *exactly* — no stray cent between the column and the bottom
 * line. That is the whole point: a client who adds the column has to reach the number we asked
 * them to sign, and "it's a rounding difference" is not an answer anyone should have to give.
 *
 * Zero weights split evenly, because a band over groups that all priced to nothing has no
 * proportion to follow and dropping it into one arbitrary group would be a lie about where it
 * came from.
 */
function allocate(total: number, weights: number[]): number[] {
	const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
	const shares: number[] = [];
	let allocated = 0;

	for (const [index, weight] of weights.entries()) {
		if (index === weights.length - 1) {
			shares.push(round2(total - allocated));
			break;
		}

		const share = round2(weightTotal === 0 ? total / weights.length : total * (weight / weightTotal));
		shares.push(share);
		allocated = round2(allocated + share);
	}

	return shares;
}

/**
 * A quote, read the way it is written: three groups, not two.
 *
 * Fixed is firm. Our call time is an estimate with its assumptions published. Carrier termination
 * is a third group rather than part of our per-minute rate — Thailand costs roughly double
 * Singapore, so blending it in would either overcharge Singapore or undercharge Thailand, and
 * passing it through itemised is both more honest and easier to defend.
 *
 * Every line shows the rate that produced it, so a client can see why a number is what it is
 * rather than being handed a total.
 *
 * The arithmetic is the other half of that. Every variable line carries the *midpoint* of what we
 * expect it to cost, and the quote's band is the midpoint of the whole variable side widened by a
 * variance — so printing group subtotals as midpoints under a total that had the variance applied
 * gave a client who added the column a different number from the one at the bottom of it, on the
 * screen they sign. Each estimated group is now shown as its own range (UX §10: "Expected range
 * S$ 620 – 925"), allocated out of the quote's own band, with the midpoint the lines add to said
 * underneath. Fixed + the group ranges = the estimated total, in the figures as printed.
 */
export function QuoteLinesTable({
	lines,
	currency,
	fixedTotal,
	variableLow,
	variableHigh,
	emptyText = "Nothing priced yet.",
}: QuoteLinesTableProps): React.JSX.Element {
	const grouped = React.useMemo(() => {
		const buckets: Record<QuoteLineGroup, QuoteLine[]> = { fixed: [], usage: [], carrier: [] };
		for (const line of lines) buckets[quoteLineGroup(line)].push(line);
		return buckets;
	}, [lines]);

	/**
	 * The band, split over the estimated groups that actually have lines.
	 *
	 * Weighted by each group's own midpoint, which is what the group's lines add up to, so the
	 * split follows the quote rather than being assumed even. Absent where the quote carries no
	 * band at all — a quote of nothing but fixed charges has no range to present, and inventing
	 * one from a null would print a range around zero.
	 */
	const bands = React.useMemo(() => {
		const low = amountOrNull(variableLow);
		const high = amountOrNull(variableHigh);
		if (low === null || high === null) return null;

		const keys = QUOTE_LINE_GROUPS.map((group) => group.key).filter(
			(key) => key !== "fixed" && grouped[key].length > 0
		);
		if (keys.length === 0) return null;

		const weights = keys.map((key) => sumAmounts(grouped[key]));
		const lows = allocate(low, weights);
		const highs = allocate(high, weights);

		return new Map(keys.map((key, index) => [key, { low: lows[index], high: highs[index], mid: weights[index] }]));
	}, [grouped, variableLow, variableHigh]);

	if (lines.length === 0) {
		return (
			<Card variant="outlined" sx={{ p: 2 }}>
				<Typography variant="body2" color="text.secondary">
					{emptyText}
				</Typography>
			</Card>
		);
	}

	// The quote's own fixed total is authoritative; the sum of the fixed lines only stands in for
	// a caller that did not pass one, and it is the same figure by construction.
	const fixed = amountOrNull(fixedTotal) ?? round2(sumAmounts(grouped.fixed));
	const total = bands
		? {
				low: round2(fixed + (amountOrNull(variableLow) ?? 0)),
				high: round2(fixed + (amountOrNull(variableHigh) ?? 0)),
			}
		: null;

	return (
		<Card variant="outlined" sx={{ overflowX: "auto" }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 600 }}>Line</TableCell>
						<TableCell sx={{ fontWeight: 600 }}>Market</TableCell>
						<TableCell sx={{ fontWeight: 600 }} align="right">
							Quantity
						</TableCell>
						<TableCell sx={{ fontWeight: 600 }} align="right">
							Unit Rate
						</TableCell>
						<TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
						<TableCell sx={{ fontWeight: 600 }} align="right">
							Amount
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{QUOTE_LINE_GROUPS.filter((group) => grouped[group.key].length > 0).map((group) => {
						const band = bands?.get(group.key) ?? null;

						return (
							<React.Fragment key={group.key}>
								<TableRow>
									<TableCell colSpan={6} sx={{ bgcolor: "background.level1" }}>
										<Typography variant="subtitle2">{group.title}</Typography>
										<Typography variant="caption" color="text.secondary">
											{group.note}
											{group.key === "fixed"
												? ""
												: " Each line below is the midpoint we expect; the range is what we would not be surprised by."}
										</Typography>
									</TableCell>
								</TableRow>

								{grouped[group.key].map((line) => (
									<TableRow key={line.id}>
										<TableCell>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{line.label}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{labelOf(PRICE_CODE_LABELS, line.code)}
											</Typography>
										</TableCell>
										<TableCell>
											{line.market ? `${line.market} · ${marketLabel(line.market)}` : "All markets"}
										</TableCell>
										<TableCell align="right">{formatQuantity(line.quantity)}</TableCell>
										<TableCell align="right">{formatRate(line.unitAmount, currency)}</TableCell>
										<TableCell>{labelOf(PRICE_UNIT_LABELS, line.unit)}</TableCell>
										<TableCell align="right">{formatMoney(line.amount, currency)}</TableCell>
									</TableRow>
								))}

								<TableRow>
									<TableCell colSpan={5} align="right">
										<Stack spacing={0.25} sx={{ alignItems: "flex-end" }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{group.key === "fixed" ? "Fixed total" : "Expected range"}
											</Typography>
											{band ? (
												<Typography variant="caption" color="text.secondary">
													the lines above add to {formatMoney(round2(band.mid), currency)}
												</Typography>
											) : null}
										</Stack>
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 600 }}>
										{group.key === "fixed"
											? formatMoney(fixed, currency)
											: band
												? formatMoneyRange(band.low, band.high, currency)
												: formatMoney(round2(sumAmounts(grouped[group.key])), currency)}
									</TableCell>
								</TableRow>
							</React.Fragment>
						);
					})}

					{total ? (
						<TableRow>
							<TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>
								Estimated total
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{formatMoneyRange(total.low, total.high, currency)}
							</TableCell>
						</TableRow>
					) : null}
				</TableBody>
			</Table>
		</Card>
	);
}
